/**
 * Private Route Wrapper - Quan ly truy cap trang
 *
 * Chuc nang:
 * - Kiem tra nguoi dung dang nhap (hasToken)
 * - Kiem tra vai tro (roles)
 * - Kiem tra quyen menu (menuKeys)
 * - Chuyen huong ve /login neu khong thap
 *
 * Su dung:
 * <PrivateRoute menuKeys={['/patients']}>...</PrivateRoute>
 */
import React from 'react';
import { Spin } from 'antd';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

interface Props {
  children: React.ReactNode;
  /** Optional: require one of these roles. Empty = any authenticated user. */
  roles?: string[];
  /** Optional: block these roles even if authenticated. */
  forbiddenRoles?: string[];
  /** Optional: require one of these menu permissions. Empty = any authenticated user. */
  menuKeys?: string[];
}

const PrivateRoute: React.FC<Props> = ({ children, roles = [], forbiddenRoles = [], menuKeys = [] }) => {
  const {
    user,
    isAuthenticated,
    hasRole,
    hasMenuAccess,
    authorizationLoaded,
    authorizationLoading,
    ensureAuthorizationLoaded,
  } = useAuthStore();
  const location = useLocation();

  React.useEffect(() => {
    if (isAuthenticated() && menuKeys.length > 0 && !authorizationLoaded && !authorizationLoading) {
      ensureAuthorizationLoaded();
    }
  }, [isAuthenticated, menuKeys.length, authorizationLoaded, authorizationLoading, ensureAuthorizationLoaded]);

  if (!isAuthenticated()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (menuKeys.length > 0 && !authorizationLoaded) {
    return <div style={{ paddingTop: 80, textAlign: 'center' }}><Spin size="large" /></div>;
  }

  if (roles.length > 0 && !hasRole(roles)) {
    return <Navigate to="/403" replace />;
  }

  if (forbiddenRoles.length > 0 && forbiddenRoles.includes(String(user?.role || '').trim())) {
    return <Navigate to="/403" replace />;
  }

  if (menuKeys.length > 0 && !hasMenuAccess(menuKeys)) {
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
};

export default PrivateRoute;
